import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle, Share2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TikTokCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [userName, setUserName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      setStatus("error");
      setErrorMsg(errorDescription || error || "Autorisation TikTok refusée");
      return;
    }

    if (!code) {
      setStatus("error");
      setErrorMsg("Aucun code d'autorisation reçu");
      return;
    }

    exchangedRef.current = true;
    exchangeCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCode = async (code) => {
    const redirectUri = `${window.location.origin}/auth/tiktok/callback`;

    try {
      const response = await axios.post(`${API}/tiktok/oauth/exchange`, {
        code,
        redirect_uri: redirectUri,
      });

      setUserName(response.data.user?.display_name || "TikTok");
      toast.success("Connexion TikTok réussie!");
      setStatus("success");
      setTimeout(() => navigate("/settings"), 1500);
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la connexion TikTok";
      if (msg.includes("has been used") || msg.includes("expired")) {
        navigate("/settings");
        return;
      }
      setStatus("error");
      setErrorMsg(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Share2 className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">SocialHub</h1>
        </div>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            {status === "loading" && (
              <div className="text-center py-8 space-y-4" data-testid="tiktok-callback-loading">
                <Loader2 className="w-12 h-12 animate-spin mx-auto" />
                <p className="text-lg font-medium">Connexion TikTok en cours...</p>
                <p className="text-sm text-muted-foreground">Récupération de vos informations</p>
              </div>
            )}

            {status === "success" && (
              <div className="text-center py-8 space-y-4" data-testid="tiktok-callback-success">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                <p className="text-lg font-medium">TikTok connecté!</p>
                <p className="text-sm text-muted-foreground">Compte : {userName}</p>
                <p className="text-xs text-muted-foreground">Redirection vers les paramètres...</p>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-8 space-y-4" data-testid="tiktok-callback-error">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <p className="text-lg font-medium">Erreur de connexion TikTok</p>
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button variant="outline" onClick={() => navigate("/settings")}>
                    Retour aux paramètres
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
